import React, { useState, useEffect } from 'react';
import { Button, Modal, Checkbox, Empty, Upload, message, Typography } from 'antd';
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import '@wangeditor/editor/dist/css/style.css';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';

const { Text } = Typography;

// 文件大小格式化
const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
};

/**
 * TAPD 内容富文本编辑器（wangEditor）
 * 支持：插入工单附件（图片嵌入 / 文件转链接）、上传图片（base64）、上传任意附件（mock）
 * 受控协议：value（html string）+ onChange（html string），可直接放入 antd Form.Item
 * onAttachmentsChange：回传富文本中上传的额外附件，用于随 TAPD 单一并创建
 */
function TapdRichEditor({ value, onChange, attachments, onAttachmentsChange }) {
  const [editor, setEditor] = useState(null);
  const [html, setHtml] = useState(value || '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [uploadedAttachments, setUploadedAttachments] = useState([]);

  // 卸载时销毁编辑器实例
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  // 上传的额外附件回传给父组件
  useEffect(() => {
    if (onAttachmentsChange) onAttachmentsChange(uploadedAttachments);
  }, [uploadedAttachments, onAttachmentsChange]);

  const toolbarConfig = {};
  const editorConfig = {
    placeholder: '请输入需求/缺陷内容，可插入图片和附件...',
    MENU_CONF: {
      uploadImage: {
        base64LimitSize: 5 * 1024 * 1024, // 5MB 内图片转 base64 直接嵌入
      },
    },
  };

  // 内容变化：空内容回传空字符串，便于 Form 的 required 校验
  const handleChange = (ed) => {
    const isEmpty = ed.isEmpty();
    const h = isEmpty ? '' : ed.getHtml();
    setHtml(h);
    if (onChange) onChange(h);
  };

  // 在光标处插入 HTML 片段
  const insertHtml = (fragment) => {
    if (!editor) return;
    editor.restoreSelection();
    editor.dangerouslyInsertHtml(fragment);
  };

  // 插入图片（src 支持 base64 或 http url）
  const insertImage = (src, alt) => {
    const safeAlt = (alt || '图片').replace(/"/g, '');
    insertHtml(`<img src="${src}" alt="${safeAlt}" style="max-width:100%;" />`);
  };

  // 插入文件链接
  const insertFileLink = (name, url) => {
    const safeName = (name || '附件').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    insertHtml(`<a href="${url || '#'}" target="_blank">📎 ${safeName}</a>`);
  };

  // 批量插入选中的工单附件
  const handleInsertPicked = () => {
    const picked = selected.map((i) => attachments[i]).filter(Boolean);
    picked.forEach((f) => {
      if (f.type === 'image') insertImage(f.url, f.name);
      else insertFileLink(f.name, f.url);
    });
    setPickerOpen(false);
    setSelected([]);
  };

  // 自定义上传附件（mock：图片转 base64 嵌入，文件仅记录名称转链接）
  const handleUpload = (file) => {
    const isImage = file.type && file.type.startsWith('image/');
    if (isImage) {
      if (file.size > 5 * 1024 * 1024) {
        message.warning('图片超过 5MB，请压缩后上传');
        return Upload.LIST_IGNORE;
      }
      const reader = new FileReader();
      reader.onload = () => {
        insertImage(reader.result, file.name);
        setUploadedAttachments(prev => [
          ...prev,
          { name: file.name, type: 'image', size: formatSize(file.size), url: reader.result },
        ]);
      };
      reader.readAsDataURL(file);
    } else {
      insertFileLink(file.name, '#');
      setUploadedAttachments(prev => [
        ...prev,
        { name: file.name, type: 'file', size: formatSize(file.size), url: '#' },
      ]);
      message.success('附件已插入（演示环境不真实上传）');
    }
    return Upload.LIST_IGNORE; // 阻止 antd 自动上传
  };

  const pickedCount = selected.length;

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      <Toolbar
        editor={editor}
        defaultConfig={toolbarConfig}
        mode="default"
        style={{ borderBottom: '1px solid #e5e5e5' }}
      />

      {/* 辅助操作栏：插入工单附件 / 上传附件 */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', padding: '4px 8px',
        borderBottom: '1px solid #f0f0f0', background: '#fafafa',
      }}>
        <Button size="small" icon={<PaperClipOutlined />} onClick={() => setPickerOpen(true)}>
          插入工单附件
        </Button>
        <Upload beforeUpload={handleUpload} showUploadList={false} multiple>
          <Button size="small" icon={<UploadOutlined />}>上传附件</Button>
        </Upload>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
          图片 ≤5MB 直接嵌入，zip/pdf 等以链接插入
        </Text>
      </div>

      <Editor
        defaultConfig={editorConfig}
        value={html}
        onCreated={setEditor}
        onChange={handleChange}
        mode="default"
        style={{ height: 280, overflowY: 'hidden' }}
      />

      {/* 工单附件选择弹窗 */}
      <Modal
        title="插入工单附件"
        open={pickerOpen}
        onOk={handleInsertPicked}
        onCancel={() => { setPickerOpen(false); setSelected([]); }}
        okText={`插入（${pickedCount}）`}
        okButtonProps={{ disabled: pickedCount === 0 }}
        cancelText="取消"
      >
        {attachments && attachments.length > 0 ? (
          <Checkbox.Group
            value={selected}
            onChange={setSelected}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {attachments.map((f, idx) => (
              <Checkbox key={idx} value={idx}>
                {f.name}
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  {f.type === 'image' ? '（将嵌入为图片）' : '（将插入为链接）'}
                </Text>
              </Checkbox>
            ))}
          </Checkbox.Group>
        ) : (
          <Empty description="该工单暂无附件" />
        )}
      </Modal>
    </div>
  );
}

export default TapdRichEditor;
